import { DrawerBody } from '@chakra-ui/react';
import {
  AppliedFilterTags,
  type Item,
} from '@foam/buoy/src/components/AppliedFilterTags/AppliedFilterTags';
import { useCallback, useMemo } from 'react';
import { Toggle } from 'ui-components';
import { CollapsibleDrawerSection } from '~/components/CollapsibleDrawerSection/CollapsibleDrawerSection';
import { ContentSearchFilterPlatformList } from '~/components/ContentSearchFilter/components/ContentSearchFilterPlatform';
import { Drawer } from '~/components/Drawer';
import { SOCIAL_PLATFORMS, SOCIAL_PLATFORMS_NAMES } from '~/constants';
import { CONTENT_SEARCH_PARAMS } from '~/hooks/useContentSearch';

import { useContentSearchFilterContext } from '../../ContentSearchFilterContext';
import { CreatorSelector } from './CreatorSelector/CreatorSelector';
import { useV2ContentFiltersStore } from './useV2ContentFiltersStore';

export const ContentFiltersV2Drawer: React.FC = () => {
  const { isGlobalFiltersDrawerOpen, closeGlobalFiltersDrawer } =
    useV2ContentFiltersStore();
  const { filters } = useContentSearchFilterContext();

  const platformSectionCount = useMemo(() => {
    let count = 0;

    if (!filters.platform) {
      return count;
    }

    if (
      filters.platform?.includes(SOCIAL_PLATFORMS.instagram) &&
      filters.channel?.length
    ) {
      // Keep in mind that we don't count Instagram as just another filter;
      // we count the channels instead. That's why we need to subtract 1
      count += filters.platform?.length - 1 + filters.channel?.length;
    } else {
      count += filters.platform?.length;
    }

    return count;
  }, [filters.channel, filters.platform]);

  const creatorSectionCount = filters?.creator ? 1 : 0;
  const adsSectionCount = filters?.ads ? 1 : 0;

  return (
    <Drawer
      isOpen={isGlobalFiltersDrawerOpen}
      title="Filters"
      onClose={closeGlobalFiltersDrawer}
      closeOnOverlayClick={isGlobalFiltersDrawerOpen}
      onOverlayClick={closeGlobalFiltersDrawer}
      contentTrackId="foam-myagency-globalTalentFilters-panel"
      contentProps={{
        width: '100%',
        maxWidth: { base: '100%', md: '360px' },
        minWidth: { base: 'unset', md: '360px' },
      }}
    >
      <DrawerBody
        sx={{ display: 'flex', flexDirection: 'column', gap: 'spacing-350' }}
      >
        <FilterBadgeListSection />

        <CollapsibleDrawerSection
          title={`By platform ${
            platformSectionCount > 0 ? `(${platformSectionCount})` : ''
          }`}
          data-testid="sidebar-filters-platform"
          defaultOpen
        >
          <ContentSearchFilterPlatformList trackId="filterbar-platform" />
        </CollapsibleDrawerSection>

        <CollapsibleDrawerSection
          title={`By talent ${
            creatorSectionCount > 0 ? `(${creatorSectionCount})` : ''
          }`}
          defaultOpen
          className="pb-4"
        >
          <CreatorSelector />
        </CollapsibleDrawerSection>

        <CollapsibleDrawerSection
          title={`By content ${
            adsSectionCount > 0 ? `(${adsSectionCount})` : ''
          }`}
          defaultOpen
        >
          <PaidContentToggle trackId="filterbar-adToggle-off" />
        </CollapsibleDrawerSection>
      </DrawerBody>
    </Drawer>
  );
};

export function PaidContentToggle({ trackId }: { trackId?: string }) {
  const {
    onChange,
    filters: { ads = false },
  } = useContentSearchFilterContext();

  const handleChange = useCallback(
    (checked: boolean) => onChange({ [CONTENT_SEARCH_PARAMS.ads]: checked }),
    [onChange],
  );
  return (
    <Toggle
      id="mytalent-toggle"
      trackId={`${trackId}-${ads ? 'on' : 'off'}`}
      label="Paid Content"
      checked={ads}
      onChange={handleChange}
    />
  );
}

function FilterBadgeListSection() {
  const { filters, onChange, selectedCreator } =
    useContentSearchFilterContext();

  const activeFilters = useMemo(() => {
    const platformItems =
      filters.platform?.map((platform) => ({
        label:
          SOCIAL_PLATFORMS_NAMES[
            platform.toLowerCase() as keyof typeof SOCIAL_PLATFORMS_NAMES
          ],
        value: `platform:${platform}`,
      })) || [];

    return [
      ...platformItems,
      ...(filters.creator && selectedCreator
        ? [
            {
              label: selectedCreator?.fullName,
              value: 'creator',
            },
          ]
        : []),
      ...(filters.ads
        ? [
            {
              label: 'Paid Content',
              value: 'ads',
            },
          ]
        : []),
    ];
  }, [filters, selectedCreator]);

  const handleFilterRemove = useCallback(
    (item: Item) => {
      if (item.value.startsWith('platform:')) {
        const platformToRemove = item.value.split(':')[1];
        const newPlatforms =
          filters.platform?.filter((p) => p !== platformToRemove) || [];

        onChange({ [CONTENT_SEARCH_PARAMS.platform]: newPlatforms });

        // We need to remove the channel filter if the platform is Instagram
        if (platformToRemove === SOCIAL_PLATFORMS.instagram) {
          onChange({ [CONTENT_SEARCH_PARAMS.channel]: [] });
        }
      } else {
        switch (item.value) {
          case 'creator':
            onChange({ [CONTENT_SEARCH_PARAMS.creator]: undefined });
            break;
          case 'ads':
            onChange({ [CONTENT_SEARCH_PARAMS.ads]: false });
            break;
        }
      }
    },
    [onChange, filters.platform],
  );

  return (
    <AppliedFilterTags
      items={activeFilters}
      onItemClick={handleFilterRemove}
      className="pb-2"
    />
  );
}
